import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import DepartureTopStrip from '../components/ui/DepartureTopStrip';
import { useAdmin } from '../contexts/AdminContext';
import { resolveSupervisorIxoraIds } from '../utils/supervisorIds';

// IST date string helper
const getISTDateString = () => {
  const now = new Date();
  const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().slice(0, 10);
};

const relativeTime = (ts) => {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diff < 2) return 'Just now';
  if (diff < 60) return `${diff}m ago`;
  return `${Math.floor(diff / 60)}h ago`;
};

// ---------------------------------------------------------------------------
// Loading bar (prefers-reduced-motion handled via CSS)
// ---------------------------------------------------------------------------

function SmvLoadingBar() {
  return (
    <>
      <style>{`
        @keyframes smv-bar { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
        @media (prefers-reduced-motion: reduce) { .smv-loading-bar-fill { display: none; } }
      `}</style>
      <div style={{ position: 'relative', height: 2, background: 'var(--c-line-soft)', overflow: 'hidden', marginBottom: 'var(--sp-4)' }}>
        <div
          className="smv-loading-bar-fill"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'var(--c-brand)',
            animation: 'smv-bar 900ms ease-in-out infinite',
          }}
        />
      </div>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '.14em',
        color: 'var(--c-ink-3)',
        textTransform: 'uppercase',
      }}>
        LOADING SUPERVISORS…
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

const trackingChip = (status) => {
  if (status === 'active' || status === 'live') {
    return (
      <span style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '.12em',
        fontWeight: 600,
        color: 'var(--c-success)',
        background: 'rgba(22,163,74,.10)',
        padding: '3px 8px',
        display: 'inline-block',
        border: '1px solid rgba(22,163,74,.20)',
      }}>
        LIVE
      </span>
    );
  }
  return (
    <span style={{
      fontFamily: 'var(--font-mono)',
      fontSize: 10,
      letterSpacing: '.12em',
      fontWeight: 600,
      color: 'var(--c-warning)',
      background: 'rgba(217,119,6,.10)',
      padding: '3px 8px',
      display: 'inline-block',
      border: '1px solid rgba(217,119,6,.20)',
    }}>
      PAUSED
    </span>
  );
};

const checklistBadge = (status) => {
  if (status === 'Done') {
    return (
      <span style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '.12em',
        fontWeight: 700,
        color: 'var(--c-success)',
      }}>
        DONE
      </span>
    );
  }
  if (status === 'Week Off') {
    return (
      <span style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '.12em',
        fontWeight: 600,
        color: 'var(--c-ink-3)',
      }}>
        WEEK OFF
      </span>
    );
  }
  return (
    <span style={{
      fontFamily: 'var(--font-mono)',
      fontSize: 10,
      letterSpacing: '.12em',
      color: 'var(--c-ink-3)',
    }}>
      —
    </span>
  );
};

export default function SiteManagerView() {
  const { siteManagerAllotment, user, isAdmin: _isAdmin } = useAdmin();
  const allotment = siteManagerAllotment;
  const _supervisorUid = user?.uid;
  const todayString = getISTDateString();
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (!allotment) {
      setLoading(false);
      setFetchError('No area allotment assigned. Contact your administrator to set up your site manager access.');
      return;
    }

    setFetchError(null);
    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        // 1. Query supervisors in this allotment
        const supRef = collection(db, 'supervisors');
        const supQuery = query(
          supRef,
          where('ixora_allotment', '==', allotment),
          orderBy('ixora_user_id'),
        );
        const supSnap = await getDocs(supQuery);
        if (cancelled) return;

        const rawSupervisors = supSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          todayCount: 0,
          totalCount: 0,
          checklistStatus: null,
        }));

        // 2 + 3. Fetch today count, total count, and checklist for each supervisor
        await Promise.all(rawSupervisors.map(async (sup) => {
          // Union over current + legacy ids so post-migration supervisors
          // still surface historical feedback in totals.
          const ids = resolveSupervisorIxoraIds(sup);
          if (ids.length === 0) return;
          const idClause = ids.length === 1
            ? where('feedback_collector_ixora_userID', '==', ids[0])
            : where('feedback_collector_ixora_userID', 'in', ids);

          try {
            const [todaySnap, totalSnap] = await Promise.all([
              getDocs(query(
                collection(db, 'feedback_collection'),
                idClause,
                where('feedback_collection_Date', '==', todayString),
              )),
              getDocs(query(
                collection(db, 'feedback_collection'),
                idClause,
              )),
            ]);
            sup.todayCount = todaySnap.size;
            sup.totalCount = totalSnap.size;
          } catch {
            // leave counts at 0
          }

          // 4. Checklist subcollection
          try {
            const clRef = collection(db, 'supervisors', sup.id, 'checklist');
            const clQuery = query(clRef, where('checklistDate', '==', todayString));
            const clSnap = await getDocs(clQuery);
            if (clSnap.size > 0) {
              let status = 'Done';
              clSnap.forEach((d) => {
                if (d.data()?.isWeekOff) status = 'Week Off';
              });
              sup.checklistStatus = status;
            } else {
              sup.checklistStatus = 'Not Done';
            }
          } catch {
            sup.checklistStatus = 'Not Done';
          }
        }));

        if (!cancelled) {
          setSupervisors([...rawSupervisors]);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('SiteManagerView load error:', err);
          if (err?.code === 'permission-denied') {
            setFetchError('You do not have permission to view this area\'s supervisors. Contact your administrator.');
          } else {
            setFetchError('Failed to load supervisors. Please refresh the page.');
          }
          setSupervisors([]);
          setLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [allotment]);

  const totalTodayFeedbacks = supervisors.reduce((acc, s) => acc + (s.todayCount || 0), 0);

  // Human-readable IST date for display
  const displayDate = (() => {
    const now = new Date();
    const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
    return ist.toLocaleDateString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
    }).toUpperCase();
  })();

  return (
    <div className="db-page">
      <DepartureTopStrip />

      {/* Ribbon */}
      <div className="db-ribbon">
        <div className="db-ribbon-title">
          <div className="db-ribbon-title-row">
            YOUR SITE —{' '}
            <em style={{ color: 'var(--c-brand)', fontStyle: 'normal', fontWeight: 700 }}>
              {allotment ? allotment.toUpperCase() : '—'}
            </em>
          </div>
          <div className="db-ribbon-caption">
            SITE MANAGER VIEW — {displayDate}
          </div>
        </div>
      </div>

      <main style={{ padding: 'var(--sp-6) var(--pad-h)', maxWidth: 1200 }}>

        {/* Stat tiles */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 'var(--sp-8)', flexWrap: 'wrap' }}>
          <div style={{
            border: '1px solid var(--c-line-soft)',
            padding: '20px 24px',
            background: 'var(--c-bg-2)',
            minWidth: 200,
          }}>
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 32,
              fontWeight: 700,
              color: 'var(--c-ink)',
              fontVariantNumeric: 'tabular-nums slashed-zero',
              lineHeight: 1,
              marginBottom: 8,
            }}>
              {loading ? '—' : totalTodayFeedbacks}
            </div>
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '.16em',
              textTransform: 'uppercase',
              color: 'var(--c-ink-3)',
            }}>
              SITE FEEDBACKS TODAY
            </div>
          </div>

          <div style={{
            border: '1px solid var(--c-line-soft)',
            padding: '20px 24px',
            background: 'var(--c-bg-2)',
            minWidth: 200,
          }}>
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 32,
              fontWeight: 700,
              color: 'var(--c-ink)',
              fontVariantNumeric: 'tabular-nums slashed-zero',
              lineHeight: 1,
              marginBottom: 8,
            }}>
              {loading ? '—' : supervisors.length}
            </div>
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '.16em',
              textTransform: 'uppercase',
              color: 'var(--c-ink-3)',
            }}>
              SUPERVISORS IN ALLOTMENT
            </div>
          </div>
        </div>

        {/* Section header with one-line summary */}
        <div style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '.18em',
          textTransform: 'uppercase',
          color: 'var(--c-ink-2)',
          fontWeight: 700,
          marginBottom: 12,
          borderBottom: '1px solid var(--c-line-hard)',
          paddingBottom: 8,
          gap: 'var(--sp-4)',
          flexWrap: 'wrap',
        }}>
          <span>SUPERVISOR STATUS</span>
          {!loading && supervisors.length > 0 && (
            <span style={{
              fontWeight: 500,
              fontSize: 10,
              letterSpacing: '.12em',
              color: 'var(--c-ink-3)',
            }}>
              {supervisors.length} SUPERVISOR{supervisors.length !== 1 ? 'S' : ''} · {totalTodayFeedbacks} FEEDBACK{totalTodayFeedbacks !== 1 ? 'S' : ''} TODAY
            </span>
          )}
        </div>

        {/* Error banner */}
        {fetchError && (
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '.08em',
            color: '#dc2626',
            background: 'rgba(220,38,38,.06)',
            border: '1px solid rgba(220,38,38,.20)',
            padding: '10px 14px',
            marginBottom: 'var(--sp-6)',
          }}>
            {fetchError}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ padding: 'var(--sp-4) 0' }}>
            <SmvLoadingBar />
          </div>
        ) : fetchError ? null : supervisors.length === 0 ? (
          <div style={{
            padding: 'var(--sp-12) var(--pad-h)',
            textAlign: 'center',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '.14em',
            color: 'var(--c-ink-3)',
            textTransform: 'uppercase',
          }}>
            <div style={{ marginBottom: 6 }}>NO SUPERVISORS IN THIS ALLOTMENT</div>
            <div style={{ fontSize: 10, letterSpacing: '.10em' }}>
              Add supervisors to {allotment ? allotment.toUpperCase() : 'your allotment'} in the admin panel
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="db-table" style={{ width: '100%', minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>NAME</th>
                  <th style={{ textAlign: 'left' }}>ID</th>
                  <th style={{ textAlign: 'center' }}>CHECKLIST</th>
                  <th className="num">TODAY</th>
                  <th className="num">TOTAL</th>
                  <th style={{ textAlign: 'left' }}>LAST SEEN</th>
                  <th style={{ textAlign: 'center' }}>TRACKING</th>
                </tr>
              </thead>
              <tbody>
                {supervisors.map((sup) => (
                  <tr key={sup.id}>
                    <td style={{
                      fontFamily: "'Geist', sans-serif",
                      fontSize: 14,
                      fontWeight: 600,
                      color: sup.userStatus === 'inactive' ? 'var(--c-ink-4)' : 'var(--c-ink)',
                    }}>
                      {sup.display_name || sup.name || '—'}{sup.userStatus === 'inactive' ? ' · [INACTIVE]' : ''}
                    </td>
                    <td style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                      color: 'var(--c-ink-2)',
                      fontVariantNumeric: 'tabular-nums slashed-zero',
                    }}>
                      {sup.ixora_user_id || '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {checklistBadge(sup.checklistStatus)}
                    </td>
                    <td className="num" style={{
                      fontFamily: 'var(--font-mono)',
                      fontVariantNumeric: 'tabular-nums slashed-zero',
                      fontWeight: 700,
                      color: 'var(--c-ink)',
                    }}>
                      {sup.todayCount ?? 0}
                    </td>
                    <td className="num" style={{
                      fontFamily: 'var(--font-mono)',
                      fontVariantNumeric: 'tabular-nums slashed-zero',
                    }}>
                      {sup.totalCount ?? 0}
                    </td>
                    <td style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      color: 'var(--c-ink-3)',
                      fontVariantNumeric: 'tabular-nums slashed-zero',
                    }}>
                      {relativeTime(sup.lastLocationUpdate || sup.lastSeen || null)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {trackingChip(sup.trackingStatus || sup.tracking_status || 'paused')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
